import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { FileText, Shield, Users, AlertTriangle, CheckCircle, XCircle, Clock, Scale, Mail } from 'lucide-react';

export default function TermsOfServices() {
 const userResponsibilities = [
 {
 role: "Job Seekers",
 icon: Users,
 responsibilities: [
 "Provide accurate and truthful information",
 "Keep your profile updated with current details",
 "Do not create multiple accounts",
 "Respect recruiters and respond professionally"
 ]
 },
 {
 role: "Recruiters/Employers",
 icon: Shield,
 responsibilities: [
 "Post genuine and legitimate job opportunities",
 "Respond to applications promptly and professionally",
 "Comply with Ethiopian labor laws and regulations",
 "Maintain fair and non-discriminatory hiring practices"
 ]
 },
 {
 role: "All Users",
 icon: AlertTriangle,
 responsibilities: [
 "Use the platform ethically and responsibly",
 "No harassment, discrimination, or abusive behavior",
 "No spamming, fraud, or malicious activities",
 "Respect intellectual property rights"
 ]
 }
 ];

 const keyTerms = [
 {
 title: "Account Registration",
 icon: Users,
 points: [
 "You must be at least 18 years old to create an account",
 "You are responsible for maintaining account confidentiality",
 "One person or entity may maintain only one account",
 "False information may result in account termination"
 ]
 },
 {
 title: "Content Ownership",
 icon: FileText,
 points: [
 "You retain ownership of content you submit",
 "By posting, you grant Shebeka a license to use your content",
 "Content must not violate any laws or third-party rights",
 "Shebeka may remove content that violates these terms"
 ]
 },
 {
 title: "Prohibited Activities",
 icon: XCircle,
 points: [
 "Posting false or misleading job listings",
 "Scraping or unauthorized data collection",
 "Impersonating others or misrepresenting affiliation",
 "Attempting to circumvent security measures"
 ]
 },
 {
 title: "Termination",
 icon: AlertTriangle,
 points: [
 "We may suspend or terminate accounts for violations",
 "You may close your account at any time",
 "Upon termination, your data will be handled per our Privacy Policy",
 "Certain obligations survive account termination"
 ]
 }
 ];

 const limitationItems = [
 "The platform is provided 'as is' without warranties",
 "We are not liable for job outcomes or hiring decisions",
 "We are not responsible for user-generated content",
 "Total liability is limited to the amount paid (if any)"
 ];

 return (
 <div className="min-h-screen ">
 {/* Hero Section */}
 <section className="relative py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center max-w-4xl mx-auto">
 <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-primary to-primary/80 shadow-lg mb-4 sm:mb-6">
 <Scale className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
 </div>
 <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6">
 Terms of Service
 </h1>
 <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8">
 Please read these terms carefully before using Shebeka's job portal platform
 </p>
 <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 text-blue-800 dark:text-blue-300 rounded-full text-xs sm:text-sm font-medium">
 Effective Date: Jan 1, 2025
 </div>
 <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 text-green-800 dark:text-green-300 rounded-full text-xs sm:text-sm font-medium">
 Governed by Ethiopian Law
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* Main Content */}
 <section className="py-12 sm:py-16 md:py-20">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Introduction */}
 <Card className="mb-8 sm:mb-12 border-primary/20">
 <CardContent className="p-4 sm:p-6 md:p-8">
 <div className="flex flex-col sm:flex-row items-start gap-4">
 <div className="flex-shrink-0">
 <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
 </div>
 <div>
 <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed mb-4">
 Welcome to <span className="font-bold text-primary">Shebeka</span>! These Terms of Service ("Terms") govern your access to and use of our job portal platform. By accessing or using Shebeka, you agree to be bound by these Terms.
 </p>
 <p className="text-sm sm:text-base text-muted-foreground">
 If you do not agree to these Terms, please do not use our platform. We reserve the right to update these Terms at any time, and your continued use constitutes acceptance of any changes.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Acceptance Notice */}
 <Card className="mb-8 sm:mb-12 bg-primary/5 border-primary/20">
 <CardContent className="p-4 sm:p-6">
 <div className="flex items-start gap-4">
 <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
 <div>
 <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">Acceptance of Terms</h3>
 <p className="text-sm sm:text-base text-muted-foreground">
 By creating an account or using any part of Shebeka, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our Privacy Policy and Cookie Policy.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* User Responsibilities */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <Users className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 User Responsibilities
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
 {userResponsibilities.map((category) => {
 const Icon = category.icon;
 return (
 <Card key={category.role} className="border-border hover:shadow-lg transition-shadow">
 <CardHeader>
 <div className="flex items-center gap-3 mb-2">
 <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
 <Icon className="w-5 h-5 text-primary" />
 </div>
 <CardTitle className="text-base sm:text-lg">{category.role}</CardTitle>
 </div>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {category.responsibilities.map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 );
 })}
 </div>
 </div>

 {/* Key Terms */}
 <div className="mb-8 sm:mb-12">
 <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
 <FileText className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
 Key Terms & Conditions
 </h2>
 <div className="space-y-4 sm:space-y-6">
 {keyTerms.map((term) => {
 const Icon = term.icon;
 return (
 <Card key={term.title} className="border-border hover:shadow-md transition-shadow">
 <CardHeader>
 <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
 <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 {term.title}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <ul className="space-y-2">
 {term.points.map((point, idx) => (
 <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-muted-foreground">
 <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
 <span className="text-xs font-bold text-primary">{idx + 1}</span>
 </div>
 <span>{point}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>
 );
 })}
 </div>
 </div>

 {/* Payment Terms (if applicable) */}
 <Card className="mb-8 sm:mb-12 border-border">
 <CardHeader>
 <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
 <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 Payment and Fees
 </CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-sm sm:text-base text-muted-foreground mb-4">
 Shebeka is currently <span className="font-semibold text-foreground">free for job seekers</span>. Recruiters and employers may be subject to fees for premium features:
 </p>
 <ul className="space-y-2">
 {[
 "All fees are displayed transparently before purchase",
 "Payments are processed securely through third-party providers",
 "Refund policy applies per our guidelines",
 "Prices subject to change with 30 days notice"
 ].map((item, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2"></div>
 {item}
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>

 {/* Limitation of Liability */}
 <Card className="mb-8 sm:mb-12 bg-yellow-500/10 border-yellow-200 dark:border-yellow-800/30">
 <CardHeader>
 <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
 <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 dark:text-yellow-400" />
 Limitation of Liability
 </CardTitle>
 <CardDescription>
 Please read this section carefully as it limits our liability
 </CardDescription>
 </CardHeader>
 <CardContent>
 <ul className="space-y-3">
 {limitationItems.map((item, idx) => (
 <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-muted-foreground">
 <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
 <span>{item}</span>
 </li>
 ))}
 </ul>
 </CardContent>
 </Card>

 {/* Dispute Resolution */}
 <Card className="mb-8 sm:mb-12 border-border">
 <CardHeader>
 <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
 <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 Dispute Resolution & Governing Law
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 <div>
 <h4 className="font-bold text-foreground mb-2 text-sm sm:text-base">Governing Law</h4>
 <p className="text-xs sm:text-sm text-muted-foreground">
 These Terms are governed by and construed in accordance with the laws of the Federal Democratic Republic of Ethiopia. Any disputes will be subject to the exclusive jurisdiction of Ethiopian courts.
 </p>
 </div>
 <div>
 <h4 className="font-bold text-foreground mb-2 text-sm sm:text-base">Dispute Resolution Process</h4>
 <ol className="space-y-2">
 {[
 "First, contact our support team to resolve informally",
 "If unresolved, we encourage mediation",
 "Legal action may be pursued as a last resort"
 ].map((step, idx) => (
 <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
 <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
 <span className="text-xs font-bold text-primary">{idx + 1}</span>
 </div>
 <span>{step}</span>
 </li>
 ))}
 </ol>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Changes to Terms */}
 <Card className="mb-8 sm:mb-12 border-border">
 <CardHeader>
 <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
 <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
 Changes to These Terms
 </CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-sm sm:text-base text-muted-foreground mb-4">
 We may update these Terms from time to time. When we make significant changes, we will notify you via:
 </p>
 <ul className="space-y-2">
 {[
 "Email notification to your registered email address",
 "Prominent notice on our platform",
 "Update notification in your dashboard"
 ].map((method, idx) => (
 <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
 <CheckCircle className="w-4 h-4 text-green-500" />
 {method}
 </li>
 ))}
 </ul>
 <p className="text-xs sm:text-sm text-muted-foreground mt-4">
 Continued use of the platform after changes constitutes acceptance of the updated Terms.
 </p>
 </CardContent>
 </Card>

 {/* Contact Information */}
 <Card className="border-border shadow-lg">
 <CardHeader>
 <CardTitle className="text-xl sm:text-2xl text-center">Questions About These Terms?</CardTitle>
 <CardDescription className="text-center">
 Contact our legal team for clarification or concerns
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
 <div className="flex items-center gap-3 p-4 rounded-lg ">
 <Mail className="w-6 h-6 text-primary" />
 <div>
 <p className="text-xs text-muted-foreground">Legal Team</p>
 <a href="mailto:legal@shebeka.com" className="text-sm sm:text-base font-medium text-primary hover:underline">
 legal@shebeka.com
 </a>
 </div>
 </div>
 <div className="flex items-center gap-3 p-4 rounded-lg ">
 <FileText className="w-6 h-6 text-primary" />
 <div>
 <p className="text-xs text-muted-foreground">Support</p>
 <a href="mailto:support@shebeka.com" className="text-sm sm:text-base font-medium text-primary hover:underline">
 support@shebeka.com
 </a>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 </section>

 {/* Final Note */}
 <section className="py-12 sm:py-16 ">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <Card className="bg-gradient-to-r from-primary to-primary/80 border-0 text-white">
 <CardContent className="p-6 sm:p-8">
 <div className="flex flex-col md:flex-row items-center justify-between gap-4">
 <div>
 <h3 className="text-lg sm:text-xl font-bold mb-2">Thank You for Using Shebeka</h3>
 <p className="text-sm sm:text-base text-white/90">
 We're committed to providing a fair, transparent, and effective job platform for all users.
 </p>
 </div>
 <div className="text-xs sm:text-sm /20 backdrop-blur-sm rounded-xl px-3 sm:px-4 py-2 text-center">
 <Clock className="w-5 h-5 mx-auto mb-1" />
 <p className="font-bold">Effective Date</p>
 <p className="text-white/80">January 1, 2025</p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Quick Links */}
 <div className="mt-8 text-center">
 <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
 Related policies and information:
 </p>
 <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
 <a 
 href="/privacy" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-card text-primary font-medium rounded-xl border border-primary/20 hover:bg-primary/5 transition-colors text-sm sm:text-base"
 >
 Privacy Policy
 </a>
 <a 
 href="/cookie-policy" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-card text-primary font-medium rounded-xl border border-primary/20 hover:bg-primary/5 transition-colors text-sm sm:text-base"
 >
 Cookie Policy
 </a>
 <a 
 href="/contact" 
 className="px-4 sm:px-6 py-2 sm:py-3 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-all text-sm sm:text-base"
 >
 Contact Us
 </a>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
}
